import { HttpClient, HttpParams } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
  BulkCorrectionInput,
  BulkCorrectionResult,
  CorrectFieldsInput,
  QueryReceptionsParams,
  ReceptionDto,
  ReceptionPageDto,
  ReceptionStatus,
  TransmissionStatus,
} from '@anessa/shared-models';

/**
 * Shared HTTP surface for the two procurement event resources (receptions and
 * expeditions), which share the same enriched DTO. Subclasses set `resource` to
 * the REST segment; only expedition-specific calls (stock-origin) are added in
 * the subclass.
 */
export abstract class WeighbridgeEventsApiBase {
  protected readonly http = inject(HttpClient);

  /** REST resource segment, e.g. `receptions` | `expeditions`. */
  protected abstract readonly resource: string;

  getList(query: QueryReceptionsParams): Observable<ReceptionPageDto> {
    let params = new HttpParams().set('plantId', query.plantId);
    // Single-valued params, sent only when set (0 counts as set for page/limit).
    const single: Record<string, string | number | null | undefined> = {
      search: query.search,
      page: query.page,
      limit: query.limit,
      sortBy: query.sortBy,
      sortDir: query.sortDir,
      dateFrom: query.dateFrom,
      dateTo: query.dateTo,
      status: query.status,
      productName: query.productName,
      transporterName: query.transporterName,
      producerName: query.producerName,
      source: query.source,
    };
    for (const [key, value] of Object.entries(single)) {
      if (value != null && value !== '') params = params.set(key, String(value));
    }
    // Repeated param, one occurrence per selected storage value.
    for (const storage of query.storage ?? []) {
      params = params.append('storage', storage);
    }
    // Repeated param, one occurrence per status — the filter takes a single value
    // today, but the type allows several (the invoicing export selects two).
    for (const status of toList(query.transmissionStatus)) {
      params = params.append('transmissionStatus', status);
    }
    if (query.includeAttachments)
      params = params.set('includeAttachments', 'true');
    if (query.includeTotals) params = params.set('includeTotals', 'true');
    return this.http.get<ReceptionPageDto>(`/api/${this.resource}`, { params });
  }

  /** Regener bulk edit (ADEV-1073): what importing the rows would change, writing nothing. */
  previewBulkCorrections(plantId: string, input: BulkCorrectionInput): Observable<BulkCorrectionResult> {
    return this.http.post<BulkCorrectionResult>(
      `/api/${this.resource}/bulk-corrections/preview?plantId=${encodeURIComponent(plantId)}`,
      input,
    );
  }

  /** Regener bulk edit (ADEV-1073): apply the valid rows with the reason. */
  applyBulkCorrections(plantId: string, input: BulkCorrectionInput): Observable<BulkCorrectionResult> {
    return this.http.post<BulkCorrectionResult>(
      `/api/${this.resource}/bulk-corrections/apply?plantId=${encodeURIComponent(plantId)}`,
      input,
    );
  }

  getOne(plantId: string, id: string): Observable<ReceptionDto> {
    return this.http.get<ReceptionDto>(`/api/${this.resource}/${id}`, {
      params: { plantId },
    });
  }

  /** Distinct "Gisement" (material) values, for the filter dropdown. */
  getGisements(plantId: string): Observable<string[]> {
    return this.http.get<string[]>(`/api/${this.resource}/gisements`, {
      params: { plantId },
    });
  }

  /** Distinct transporter names, for the filter dropdown. */
  getTransporterNames(plantId: string): Observable<string[]> {
    return this.http.get<string[]>(`/api/${this.resource}/transporters`, {
      params: { plantId },
    });
  }

  /** Distinct producer/party names, for the filter dropdown. */
  getProducerNames(plantId: string): Observable<string[]> {
    return this.http.get<string[]>(`/api/${this.resource}/producer-names`, {
      params: { plantId },
    });
  }

  setStatus(
    plantId: string,
    id: string,
    status: ReceptionStatus,
  ): Observable<ReceptionDto> {
    return this.http.patch<ReceptionDto>(
      `/api/${this.resource}/${id}/status?plantId=${encodeURIComponent(plantId)}`,
      { status },
    );
  }

  /** Persist the editable transmission status ("Statut transmission"). */
  setTransmissionStatus(
    plantId: string,
    id: string,
    transmissionStatus: TransmissionStatus,
  ): Observable<ReceptionDto> {
    return this.http.patch<ReceptionDto>(
      `/api/${this.resource}/${id}/transmission-status?plantId=${encodeURIComponent(plantId)}`,
      { transmissionStatus },
    );
  }

  /**
   * Admin correction of imported fields, with a required reason (ADEV-1075).
   * A record already sent answers 409 until `confirmExported` is set (global
   * admins only).
   */
  correctFields(
    plantId: string,
    id: string,
    input: CorrectFieldsInput,
  ): Observable<ReceptionDto> {
    return this.http.patch<ReceptionDto>(
      `/api/${this.resource}/${id}/fields?plantId=${encodeURIComponent(plantId)}`,
      input,
    );
  }
}

/** A single value or a list, always as a list; null/undefined as empty. */
function toList<T>(value: T | T[] | undefined | null): T[] {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}
